import { LicenseIcon } from '@hugeicons/core-free-icons';
import { useEffect, useRef, useState } from 'react';
import { Button } from '../ui/Button';
import { HugeIcon } from '../ui/HugeIcon';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { cn } from '../../lib/cn';

export function SeatUsageCard({
  actionLabel,
  compact = false,
  limit,
  onAction,
  used,
}: {
  actionLabel?: string;
  compact?: boolean;
  limit: number;
  onAction?: () => void;
  used: number;
}) {
  const ratio = limit > 0 ? Math.min(1, used / limit) : 0;
  const meterRef = useRef<HTMLDivElement>(null);
  const [segmentCount, setSegmentCount] = useState(20);
  const filledSegments = ratio * segmentCount;

  useEffect(() => {
    if (compact || !meterRef.current) return;

    const meter = meterRef.current;
    const updateSegmentCount = (width: number) => {
      const nextCount = Math.max(16, Math.min(48, Math.floor((width + 3) / 15)));
      setSegmentCount((current) => current === nextCount ? current : nextCount);
    };
    const observer = new ResizeObserver((entries) => updateSegmentCount(entries[0]?.contentRect.width ?? meter.clientWidth));

    updateSegmentCount(meter.clientWidth);
    observer.observe(meter);
    return () => observer.disconnect();
  }, [compact]);

  return (
    <Card className={cn(
      'relative gap-3 overflow-hidden py-3',
      compact
        ? 'rounded-[14px] bg-white before:pointer-events-none before:absolute before:inset-0 before:opacity-30 before:[background-image:linear-gradient(135deg,rgba(15,23,42,0.025)_1px,transparent_1px)] before:[background-size:12px_12px]'
        : ''
    )} size="sm">
      <CardHeader className="relative px-3">
        <CardTitle className="flex items-center gap-2 text-[13px] font-medium">
          <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-violet-500/14 text-violet-700">
            <HugeIcon icon={LicenseIcon} size={15} strokeWidth={1.9} />
          </span>
          Licensed seats
        </CardTitle>
      </CardHeader>
      <CardContent className="relative px-3">
        <div className="mb-2 flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Seats used</span>
          <span className="font-semibold tabular-nums">{used} <span className="font-normal text-muted-foreground">of {limit}</span></span>
        </div>
        <div
          aria-label={`${used} of ${limit} seats used`}
          className="grid gap-[3px]"
          ref={meterRef}
          role="img"
          style={{ gridTemplateColumns: `repeat(${compact ? 20 : segmentCount}, minmax(0, 1fr))` }}
        >
          {Array.from({ length: compact ? 20 : segmentCount }, (_, index) => {
            const segmentFill = Math.max(0, Math.min(1, filledSegments - index));
            return (
              <span className={cn('overflow-hidden border border-slate-200 bg-slate-100', compact ? 'h-3.5 rounded-[4px]' : 'h-6 rounded-[5px]')} key={index}>
                <span aria-hidden="true" className="block h-full bg-teal-600" style={{ width: `${segmentFill * 100}%` }} />
              </span>
            );
          })}
        </div>
        {!compact ? <p className="mt-2 text-[11px] text-muted-foreground">{Math.max(0, limit - used)} seats available</p> : null}
        {onAction && actionLabel ? (
          <Button className="mt-3 h-9 w-full rounded-[11px] shadow-[inset_0_1px_0_rgba(255,255,255,0.16),0_2px_5px_rgba(15,23,42,0.16)]" onClick={onAction} type="button">
            {actionLabel}
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}
